import axios from 'axios'
import keycloakConfig from '../../keycloak'
import { logout } from './keycloakSlice'
import { store } from '../store'
import i18n from '../../i18n'

export const refreshToken = async (): Promise<string | null> => {
  try {
    const refresh_token = localStorage.getItem('refreshToken')
    if (!refresh_token)
      throw new Error('Refresh token missing. Please log in again.')

    // Defaults to the env-configured client when not set (unchanged
    // behaviour); only differs for self-service organizations' own
    // (public, secret-less) client — see redux/keycloak/login.ts.
    const clientId = localStorage.getItem('tenantClientId') || keycloakConfig.clientId
    const isDefaultClient = clientId === keycloakConfig.clientId
    const params: Record<string, string> = {
      client_id: clientId,
      grant_type: 'refresh_token',
      refresh_token,
    }
    if (isDefaultClient) {
      params.client_secret = keycloakConfig.clientSecret
    }

    const response = await axios.post(
      `${keycloakConfig.url}/protocol/openid-connect/token`,
      new URLSearchParams(params),
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    )

    if (response.status !== 200)
      throw new Error('Token refresh failed. Please log in again.')

    const { access_token: newAccessToken, refresh_token: newRefreshToken } =
      response.data

    localStorage.setItem('token', newAccessToken)
    localStorage.setItem('refreshToken', newRefreshToken)
    localStorage.setItem('lastRefreshTime', Date.now().toString()) // <--- CRITICAL FIX

    return newAccessToken
  } catch (error) {
    console.error('Refresh token error:', error)
    logoutAndClearStorage(i18n.t('sessionAlerts.sessionExpired.title'))
    return null
  }
}

let isLoggingOut = false

/**
 * Injects a lightweight, theme-consistent session-expired banner into the DOM.
 */
function showSessionExpiredBanner(message: string) {
  const existing = document.getElementById('__session-expired-banner__')
  if (existing) existing.remove()

  const overlay = document.createElement('div')
  overlay.id = '__session-expired-banner__'
  overlay.className = 'sa-overlay'
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '999999',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: 'rgba(11, 30, 45, 0.45)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  })

  overlay.innerHTML = `
    <div style="
      width: min(420px, 100%);
      background: #fff;
      border-radius: 14px;
      overflow: hidden;
      box-shadow: 0 30px 80px -24px rgba(0,0,0,0.45);
      font-family: Lato, sans-serif;
    ">
      <div style="display:flex; align-items:flex-start; gap:12px; padding:20px 22px 0;">
        <div style="
          width:40px; height:40px; border-radius:12px; flex-shrink:0;
          display:grid; place-items:center; background:#fae6e3; color:#d14a3e;
        ">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <rect x="5" y="11" width="14" height="10" rx="2"/>
            <path d="M8 11V8a4 4 0 0 1 8 0v3"/>
          </svg>
        </div>
        <div style="min-width:0; flex:1;">
          <p style="margin:0 0 4px; font-size:11px; font-weight:800; letter-spacing:0.06em; text-transform:uppercase; color:#d14a3e;">
            ${i18n.t('sessionAlerts.sessionExpired.badge')}
          </p>
          <h2 style="margin:0; font-size:17px; font-weight:700; color:#00146d;">
            ${message}
          </h2>
          <p style="margin:4px 0 0; font-size:12.5px; line-height:1.4; color:#5f7484;">
            ${i18n.t('sessionAlerts.sessionExpired.message')}
          </p>
        </div>
      </div>
      <div style="
        margin-top:18px; padding:14px 22px; background:#f7fafa;
        border-top:1px solid #dfe7e8;
      ">
        <button id="__session-expired-login-btn__" style="
          width:100%; height:40px; border:none; border-radius:8px;
          background:#00146d; color:#fff; font-size:14px; font-weight:700;
          font-family:Lato, sans-serif; cursor:pointer;
        ">
          ${i18n.t('sessionAlerts.sessionExpired.loginButton')}
        </button>
      </div>
    </div>
  `

  document.body.appendChild(overlay)

  const btn = document.getElementById('__session-expired-login-btn__')
  if (btn) {
    btn.addEventListener('click', () => {
      overlay.remove()
    })
  }
}

export const logoutAndClearStorage = (message?: string) => {
  if (isLoggingOut) return // Prevent multiple calls
  isLoggingOut = true

  if (message) {
    showSessionExpiredBanner(message)
  }

  localStorage.removeItem('token')
  localStorage.removeItem('lastRefreshTime')
  localStorage.removeItem('roles')
  localStorage.removeItem('tenantClientId')
  store.dispatch(logout())

  // Navigation is handled by the banner button (or falls through after 4s)
  setTimeout(() => {
    window.location.href = '/login'
  }, 4000)
}
